import React, { useEffect, useState } from "react";
import { getContainer } from "../../ioc/IOCSetup";
import {
    AwardBonusOfferRequest, BonusConfig, BonusOffer, BonusOfferListResponse,
    BonusOfferStatus, BonusStatus, BonusVisibilityStatus
} from "../../http/protocol";
import { BonusOfferService } from "../../services/BonusOfferService";
import { BonusConfigService } from "../../services/BonusConfigService";
import { Form, FormListener } from "../form/Form";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { SelectInput } from "../form/SelectInput";
import { Submit } from "../form/Submit";
import { UserActionContainerProps, UpdateActionStatus, UserActionContainer } from "./UserActionContainer";

export const BonusAward: React.FC<UserActionContainerProps> = (props: UserActionContainerProps) => {
    const bonusOfferService: BonusOfferService = getContainer().getBonusOfferService();
    const bonusAwardService: BonusOfferService = getContainer().getBonusOfferService();
    const bonusConfigService: BonusConfigService = getContainer().getBonusConfigService();

    const [error, setError] = useState("");
    const [bonusConfigs, setBonusConfigs] = useState<BonusConfig[]>([]);
    const [form, setForm] = useState<AwardBonusOfferRequest>({});
    const [selectedBonus, setSelectedBonus] = useState<BonusConfig | undefined>(undefined);
    const [amount, setAmount] = useState(0);

    function loadContent() {
        bonusConfigService.list({
            bonusVisibilityStatus: BonusVisibilityStatus.NON_ARCHIVED,
            status: BonusStatus.ENABLED,
            ascending: false,
            orderBy: 'id',
            limit: 100
        }).then((resp) => {
            console.log("Bonus configs", resp);
            if (resp.items && resp.items.length > 0) {
                let bonuses: BonusConfig[] = resp!.items!.filter((bonus) => {
                    return bonus.status === "ENABLED" && bonus.monetaryConfig;
                });
                setBonusConfigs(bonuses);
            }
        }).catch((error) => {
            setError(error.message);
        });
    }

    useEffect(() => {
        loadContent();
    }, []);

    function getBonusDetails() {
        if (selectedBonus && selectedBonus.monetaryConfig) {
            const conf = selectedBonus.monetaryConfig;
            return (
                <tbody>
                    <tr>
                        <td colSpan={1}>Balance</td>
                        <td colSpan={3}>{`${conf.awardTarget} ${conf.currencyCode}`}</td>
                        <td colSpan={6}><span></span></td>
                    </tr>
                    <tr>
                        <td>Award Amount Fraction</td>
                        <td>{conf.awardAmountFraction}</td>
                        <td>
                            <span className="help-block">Multiplied with the input amount (ie deposit amount) to set how much bonus the player will receive. If set to 2 the user will receive 200% bonus.</span>
                        </td>
                    </tr>
                    <tr>
                        <td>Turnover Amount Factor</td>
                        <td>{conf.turnoverRequirementFactor}</td>
                        <td>
                            <span className="help-block">How many times the bonus a player is awarded he needs to turnover the bonus for it to be completed</span>
                        </td>
                    </tr>
                </tbody>
            );
        } else {
            return <tbody />;
        }
    }

    function findBonusOffer(userId: number, bonusConfig: BonusConfig): Promise<any | undefined> {
        return new Promise<any | undefined>(async (resolve, reject) => {
            try {
                let bonusOffers: BonusOfferListResponse = await bonusOfferService.list({ userId: userId, limit: 10, offset: 0, orderBy: "timestamp", ascending: false });
                resolve(bonusOffers!.items!.find((bonusOffer: BonusOffer) => {
                    return bonusOffer.bonusConfig!.id === bonusConfig.id;
                }));
            } catch (error) {
                resolve(undefined);
            }
        });
    }

    function cancelBonusOffer(userId: number, bonusOffer: BonusOffer): Promise<any> {
        let bonusConfig = bonusOffer.bonusConfig!;
        return new Promise<{ status: UpdateActionStatus; description: string; response: any; }>(async (resolve, reject) => {
            try {
                bonusOffer.status = BonusOfferStatus.CANCELED;
                let response = await bonusOfferService.save(bonusOffer);
                resolve({ status: UpdateActionStatus.OK, description: `Cancelled bonus "${bonusConfig.name}"`, response: response });
            } catch (error) {
                if (error && error.status === 403) {
                    resolve({ status: UpdateActionStatus.DENIED, description: `Failed to cancel bonus with bonus config "${bonusConfig.name}". User does not have permission to cancel bonus. Error ${error.message}`, response: error });
                } else {
                    resolve({ status: UpdateActionStatus.FAIL, description: `Failed to cancel bonus with bonus config "${bonusConfig.name}". Error ${error.message}`, response: error });
                }
            }
        });
    }

    async function cancelBonus() {
        if (!selectedBonus || !props.users) return;
        let users = [...props.users || []];
        for (let i = 0; i < users.length; i++) {
            users[i].description = `Unable to find active bonus offer with config called "${selectedBonus.name}" so will be skipped`;
            users[i].status = UpdateActionStatus.DENIED;
            let bonusOffer: BonusOffer = await findBonusOffer(users[i]!.user!.id!, selectedBonus);
            if (!bonusOffer) continue;
            if (bonusOffer.status === BonusOfferStatus.OPEN) {
                users[i].request = () => cancelBonusOffer(users[i]!.user!.id!, bonusOffer!);
                users[i].description = `Prepared to cancel bonus offer "${selectedBonus.name}"`;
                users[i].status = UpdateActionStatus.PENDING;
            } else {
                users[i].description = `Bonus offer "${selectedBonus.name}" status is ${bonusOffer.status} so will be skipped`;
                users[i].status = UpdateActionStatus.DENIED;
            }
        }

        if (props.updateUsers) props.updateUsers(users);
    }

    function awardBonus(userId: number, bonusConfig: BonusConfig, amount: number): Promise<any> {
        return new Promise<{ status: UpdateActionStatus; description: string; response: any; }>(async (resolve, reject) => {
            try {
                let response = await bonusAwardService.awardUserBonus({ bonusConfigId: bonusConfig.id, amount: amount, userId: userId });
                resolve({ status: UpdateActionStatus.OK, description: `Awarded bonus "${bonusConfig.name}" with amount "${amount}"`, response: response });
            } catch (error) {
                if (error && error.status === 403) {
                    resolve({ status: UpdateActionStatus.DENIED, description: `Failed to award bonus "${bonusConfig.name}" with amount "${amount}". User does not have permission to award bonus. Error ${error.message}`, response: error });
                } else {
                    resolve({ status: UpdateActionStatus.FAIL, description: `Failed to award bonus "${bonusConfig.name}" with amount "${amount}". Error ${error.message}`, response: error });
                }
            }
        });
    }

    const ref: FormListener<AwardBonusOfferRequest> = {
        formDidUpdate: (formModel: AwardBonusOfferRequest, validationMap: any) => {
            console.log("Bonus award formDidUpdate", formModel);
            setForm(formModel);
            let selectedBonus = bonusConfigs.find((bonus) => bonus.id == formModel.bonusConfigId);
            setSelectedBonus(selectedBonus);
            let submittedAmount = Number(formModel.amount);
            if (submittedAmount) {
                let amount = submittedAmount && selectedBonus && selectedBonus.monetaryConfig && selectedBonus.monetaryConfig.awardAmountFraction && (submittedAmount * selectedBonus.monetaryConfig.awardAmountFraction);
                setAmount(amount || 0);
            } else {
                setAmount(0);
            }
        },
        onSubmit: () => {
            if (!selectedBonus || !props.users) return;
            if(!amount || amount <= 0) return;
            let users = [...props.users || []];
            for (let i = 0; i < users.length; i++) {
                users[i].status = UpdateActionStatus.PENDING;
                users[i].request = () => awardBonus(users[i]!.user!.id!, selectedBonus, amount);
                users[i].description = `Prepared to award bonus "${selectedBonus.name}" with amount "${amount}"`;
            }
            if (props.updateUsers) props.updateUsers(users);
        }
    };

    return (
        <UserActionContainer title={"Bonus Award"} error={error}>
            <Form className="form-horizontal" formListener={ref} modelObject={form} validationMap={{}}>
                <FormGroup className="form-group mb-3 row" model="bonusConfigId">
                    <label className="col-sm-12">Bonus Configs</label>
                    <SelectInput className="form-control col-sm-8" model="bonusConfigId"
                        optional={true} values={bonusConfigs}
                        displayValue={(a) => { return a && a.name ? a.name.toString() : ""; }}
                        optionModelValue={(a: BonusConfig) => { return a.id; }} />
                    <div className="col-sm-4">
                        <Submit className="btn btn-danger" onClick={(e) => { e.preventDefault(); cancelBonus(); }}>Remove
                        </Submit>
                    </div>
                    <div className="col-sm-12">
                        <span className={"help-block"}>Choose a bonus to award or remove. Current selected bonus: {selectedBonus && selectedBonus.name}</span>
                    </div>
                </FormGroup>
                <FormGroup className="form-group mb-3 row">
                    <label className="col-sm-12">Bonus Details</label>
                    <table className={"table table-striped"}>
                        {getBonusDetails()}
                    </table>
                </FormGroup>
                <FormGroup className="form-group row" model="amount">
                    <label className="col-sm-12">Input Amount</label>
                    <div className="col-sm-6">
                        <FormInput data-testid="amount" className="form-control" model="amount" type="number" />
                    </div>
                    <div className="col-sm-6">
                        <Submit className="btn btn-primary">Award {(
                            amount && amount >= 0 && selectedBonus && selectedBonus.monetaryConfig ? `${amount} ${selectedBonus.monetaryConfig.currencyCode}` : ""
                        )}
                        </Submit>
                    </div>
                    <div className="col-sm-12">
                        <span className={"help-block"}>Prepare task for all users in the list</span>
                    </div>
                </FormGroup>
            </Form>
        </UserActionContainer>
    );
};
